import { useState, useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { useApi } from 'hooks/useApi';
import { isWithinTimeWindow } from 'utils/utils';
import moment from "moment-timezone";

// Configuration for the consultation watcher
const WATCHER_CONFIG = {
    // How often to check for upcoming consultations
    refreshInterval: 5 * 60 * 1000, // 5 minutes
    // How often to run the check logic
    checkInterval: 60 * 1000, // 1 minute
    // The buffer in minutes before and after a consultation to consider it "active"
    callBufferMinutes: 5,
    // How long to wait before cleaning up the PeerJS connection after a call window has passed
    cleanupDelay: 10 * 1000, // 10 seconds
};

/**
 * @typedef {'IDLE' | 'ACTIVE_WINDOW' | 'NO_UPCOMING_CALLS'} WatcherStatus
 */

/**
 * Custom hook to monitor upcoming consultations and determine if a call should be active.
 * @returns {{status: WatcherStatus, upcomingCall: object | null}}
 */
export function useConsultationWatcher() {
    const { data: session } = useSession();
    const userRole = session?.user?.role;

    const [status, setStatus] = useState('IDLE');
    const [upcomingCall, setUpcomingCall] = useState(null);

    const cleanupTimeoutRef = useRef(null);

    // Determine the correct API endpoint based on the user's role
    let consultationApiUrl = null;
    // Only set the URL for customer and lawyer roles to avoid unnecessary calls for admin
    if (userRole && ["customer", "lawyer"].includes(userRole)) {
        switch (userRole) {
            case 'lawyer':
                consultationApiUrl = `/consultation/lawyer?status=upcoming&type=online&limit=3`;
                break;
            case 'customer':
            default:
                consultationApiUrl = `/consultation?status=upcoming&type=online&limit=3`;
        }
    }

    const { data: consultations } = useApi(consultationApiUrl, {
        refreshInterval: WATCHER_CONFIG.refreshInterval,
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
    });

    useEffect(() => {
        // If there's no valid URL, it means the user's role is not one that
        // should be watching for consultations (e.g., admin).
        if (!consultationApiUrl) {
            setStatus('NO_UPCOMING_CALLS');
            setUpcomingCall(null);
            return;
        }

        const checkConsultations = () => {
            console.log('[ConsultationWatcher] Checking consultations...', consultations?.docs); // <-- ADDED THIS LOG
            const hasUpcomingConsultations = consultations?.docs && consultations.docs.length > 0;

            if (!hasUpcomingConsultations) {
                setStatus(prevStatus => {
                    if (prevStatus !== 'NO_UPCOMING_CALLS') {
                        console.log('[ConsultationWatcher] Status -> NO_UPCOMING_CALLS');
                    }
                    return 'NO_UPCOMING_CALLS';
                });
                setUpcomingCall(null);
                return;
            }

            const activeCalls = consultations.docs.filter(call =>
                isWithinTimeWindow(call.startTime, call.endTime, call.date, WATCHER_CONFIG.callBufferMinutes)
            );

            if (activeCalls.length > 0) {
                // Sort the active calls by their start time to find the one that's starting soonest.
                activeCalls.sort((a, b) => {
                    const startTimeA = moment.utc(`${moment.utc(a.date).format('YYYY-MM-DD')}T${a.startTime}`);
                    const startTimeB = moment.utc(`${moment.utc(b.date).format('YYYY-MM-DD')}T${b.startTime}`);
                    return startTimeA.diff(startTimeB);
                });

                const nextCall = activeCalls[0];

                // We are within the time window for a call
                if (cleanupTimeoutRef.current) {
                    clearTimeout(cleanupTimeoutRef.current);
                    cleanupTimeoutRef.current = null;
                }
                setStatus(prevStatus => {
                    if (prevStatus !== 'ACTIVE_WINDOW') {
                        console.log('[ConsultationWatcher] Status -> ACTIVE_WINDOW');
                    }
                    return 'ACTIVE_WINDOW';
                });
                setUpcomingCall(nextCall);
            } else {
                // There are consultations, but none are active right now
                setStatus(prevStatus => {
                    if (prevStatus !== 'IDLE') {
                        console.log('[ConsultationWatcher] Status -> IDLE');
                    }
                    return 'IDLE';
                });
                setUpcomingCall(null);
            }
        };

        const intervalId = setInterval(checkConsultations, WATCHER_CONFIG.checkInterval);
        checkConsultations(); // Run immediately on mount

        return () => clearInterval(intervalId);
    }, [consultations]);

    return { status, upcomingCall };
}
